import type {
  FacilityOperatingHoursChangeCommand,
  FacilityOperatingHoursSnapshot,
} from "@bw/shared/types";

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const OPERATING_HOURS_DAYS: ReadonlyArray<{
  dayOfWeek: DayOfWeek;
  label: string;
}> = [
  { dayOfWeek: 0, label: "Sunday" },
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
  { dayOfWeek: 6, label: "Saturday" },
];

export interface OperatingHoursWindowDraft {
  id: string;
  opensAt: string;
  closesAt: string;
}

export interface OperatingHoursDayDraft {
  enabled: boolean;
  windows: OperatingHoursWindowDraft[];
}

export type OperatingHoursWeekDraft = Record<
  DayOfWeek,
  OperatingHoursDayDraft
>;

export interface OperatingHoursScheduleDraft {
  id: string;
  effectiveFrom: string;
  effectiveUntil: string;
  week: OperatingHoursWeekDraft;
}

export interface OperatingHoursDateOverrideDraft {
  id: string;
  localDate: string;
  kind: "closed" | "replace";
  windows: OperatingHoursWindowDraft[];
}

export interface OperatingHoursClosureDraft {
  id: string;
  startsOn: string;
  endsOn: string;
  reason: string;
}

export interface OperatingHoursDraft {
  reservationStateRevision: number;
  schedules: OperatingHoursScheduleDraft[];
  dateOverrides: OperatingHoursDateOverrideDraft[];
  closurePeriods: OperatingHoursClosureDraft[];
}

let nextDraftId = 0;

function draftId(prefix: string): string {
  nextDraftId += 1;
  return `${prefix}-${nextDraftId}`;
}

function defaultWindow(prefix: string): OperatingHoursWindowDraft {
  return { id: draftId(prefix), opensAt: "09:00", closesAt: "21:00" };
}

export function emptyOperatingHoursWeek(): OperatingHoursWeekDraft {
  return {
    0: { enabled: false, windows: [defaultWindow("sun")] },
    1: { enabled: false, windows: [defaultWindow("mon")] },
    2: { enabled: false, windows: [defaultWindow("tue")] },
    3: { enabled: false, windows: [defaultWindow("wed")] },
    4: { enabled: false, windows: [defaultWindow("thu")] },
    5: { enabled: false, windows: [defaultWindow("fri")] },
    6: { enabled: false, windows: [defaultWindow("sat")] },
  };
}

export function emptyOperatingHoursDraft(
  reservationStateRevision = 0,
): OperatingHoursDraft {
  return {
    reservationStateRevision,
    schedules: [
      {
        id: draftId("schedule"),
        effectiveFrom: "",
        effectiveUntil: "",
        week: emptyOperatingHoursWeek(),
      },
    ],
    dateOverrides: [],
    closurePeriods: [],
  };
}

export function newScheduleDraft(): OperatingHoursScheduleDraft {
  return {
    id: draftId("schedule"),
    effectiveFrom: "",
    effectiveUntil: "",
    week: emptyOperatingHoursWeek(),
  };
}

export function newDateOverrideDraft(): OperatingHoursDateOverrideDraft {
  return {
    id: draftId("override"),
    localDate: "",
    kind: "closed",
    windows: [defaultWindow("override")],
  };
}

export function newClosureDraft(): OperatingHoursClosureDraft {
  return {
    id: draftId("closure"),
    startsOn: "",
    endsOn: "",
    reason: "",
  };
}

function draftWindow(
  prefix: string,
  opensAt: string,
  closesAt: string,
): OperatingHoursWindowDraft {
  return { id: draftId(prefix), opensAt, closesAt };
}

export function operatingHoursDraftFromSnapshot(
  snapshot: FacilityOperatingHoursSnapshot,
): OperatingHoursDraft {
  return {
    reservationStateRevision: snapshot.reservation_state_revision,
    schedules: snapshot.schedules.map((schedule) => {
      const week = emptyOperatingHoursWeek();
      for (const window of schedule.weekly_windows) {
        const day = window.day_of_week as DayOfWeek;
        const current = week[day];
        week[day] = {
          enabled: true,
          windows: [
            ...(current.enabled ? current.windows : []),
            draftWindow(
              `schedule-${schedule.schedule_id}-${day}`,
              window.opens_at,
              window.closes_at,
            ),
          ],
        };
      }
      for (const { dayOfWeek } of OPERATING_HOURS_DAYS) {
        if (!week[dayOfWeek].enabled) {
          week[dayOfWeek] = {
            enabled: false,
            windows: [defaultWindow(`schedule-${schedule.schedule_id}-${dayOfWeek}`)],
          };
        }
      }
      return {
        id: String(schedule.schedule_id),
        effectiveFrom: schedule.effective_from,
        effectiveUntil: schedule.effective_until ?? "",
        week,
      };
    }),
    dateOverrides: snapshot.date_overrides.map((override) => {
      const windows = override.windows ?? [];
      return {
        id: String(override.override_id),
        localDate: override.local_date,
        kind: override.kind,
        windows:
          windows.length > 0
            ? windows.map((window) =>
                draftWindow(
                  `override-${override.override_id}`,
                  window.opens_at,
                  window.closes_at,
                ),
              )
            : [defaultWindow(`override-${override.override_id}`)],
      };
    }),
    closurePeriods: snapshot.closure_periods.map((closure) => ({
      id: String(closure.closure_id),
      startsOn: closure.starts_on,
      endsOn: closure.ends_on,
      reason: closure.reason ?? "",
    })),
  };
}

function isClock(value: string, allowEndOfDay: boolean): boolean {
  if (allowEndOfDay && value === "24:00") return true;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function clockMinutes(value: string): number {
  if (value === "24:00") return 24 * 60;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function validateWindows(
  windows: readonly OperatingHoursWindowDraft[],
  label: string,
): string | null {
  if (windows.length === 0) return `${label}: add at least one time window.`;
  if (windows.length > 4) return `${label}: add no more than four windows.`;
  const intervals: Array<{ start: number; end: number }> = [];
  for (const window of windows) {
    if (
      !isClock(window.opensAt, false) ||
      !isClock(window.closesAt, true) ||
      window.opensAt === window.closesAt
    ) {
      return `${label}: use HH:MM times; 24:00 is valid only as a close.`;
    }
    const start = clockMinutes(window.opensAt);
    let end = clockMinutes(window.closesAt);
    if (end <= start && end !== 24 * 60) end += 24 * 60;
    intervals.push({ start, end });
  }
  intervals.sort((left, right) => left.start - right.start);
  for (let index = 1; index < intervals.length; index += 1) {
    if (intervals[index].start < intervals[index - 1].end) {
      return `${label}: time windows cannot overlap.`;
    }
  }
  return null;
}

export function validateOperatingHoursDraft(
  draft: OperatingHoursDraft,
): string | null {
  if (draft.schedules.length === 0) {
    return "Add at least one effective weekly schedule.";
  }
  const schedules = [...draft.schedules].sort((left, right) =>
    left.effectiveFrom.localeCompare(right.effectiveFrom),
  );
  let previousEnd = "";
  for (const schedule of schedules) {
    if (!schedule.effectiveFrom) {
      return "Each weekly schedule needs an effective start date.";
    }
    if (
      schedule.effectiveUntil &&
      schedule.effectiveUntil <= schedule.effectiveFrom
    ) {
      return "A schedule end date must be after its effective start date.";
    }
    if (previousEnd && schedule.effectiveFrom < previousEnd) {
      return "Effective weekly schedules cannot overlap.";
    }
    if (!previousEnd && schedules.indexOf(schedule) > 0) {
      return "An open-ended schedule cannot be followed by another schedule.";
    }
    previousEnd = schedule.effectiveUntil;
    const enabledDays = OPERATING_HOURS_DAYS.filter(
      ({ dayOfWeek }) => schedule.week[dayOfWeek].enabled,
    );
    if (enabledDays.length === 0) {
      return "Each weekly schedule needs at least one open day.";
    }
    for (const { dayOfWeek, label } of OPERATING_HOURS_DAYS) {
      const day = schedule.week[dayOfWeek];
      if (!day.enabled) continue;
      const error = validateWindows(day.windows, `${label} schedule`);
      if (error) return error;
    }
  }

  const overrideDates = new Set<string>();
  for (const override of draft.dateOverrides) {
    if (!override.localDate) return "Each date override needs a date.";
    if (overrideDates.has(override.localDate)) {
      return "Only one date override is allowed for each date.";
    }
    overrideDates.add(override.localDate);
    if (override.kind === "replace") {
      const error = validateWindows(override.windows, "Date override");
      if (error) return error;
    }
  }

  for (const closure of draft.closurePeriods) {
    if (!closure.startsOn || !closure.endsOn) {
      return "Each closure period needs a start and end date.";
    }
    if (closure.endsOn < closure.startsOn) {
      return "A closure period cannot end before it starts.";
    }
    if (
      [...overrideDates].some(
        (localDate) =>
          localDate >= closure.startsOn && localDate <= closure.endsOn,
      )
    ) {
      return "A date override cannot fall inside a closure period.";
    }
  }
  return null;
}

export function operatingHoursDraftToCommand(
  draft: OperatingHoursDraft,
): FacilityOperatingHoursChangeCommand {
  return {
    expected_reservation_state_revision: draft.reservationStateRevision,
    schedules: draft.schedules.map((schedule) => ({
      effective_from: schedule.effectiveFrom,
      effective_until: schedule.effectiveUntil || null,
      weekly_windows: OPERATING_HOURS_DAYS.flatMap(({ dayOfWeek }) => {
        const day = schedule.week[dayOfWeek];
        if (!day.enabled) return [];
        return day.windows.map((window) => ({
          day_of_week: dayOfWeek,
          opens_at: window.opensAt,
          closes_at: window.closesAt,
        }));
      }),
    })),
    date_overrides: draft.dateOverrides.map((override) => ({
      local_date: override.localDate,
      kind: override.kind,
      windows:
        override.kind === "replace"
          ? override.windows.map((window) => ({
              opens_at: window.opensAt,
              closes_at: window.closesAt,
            }))
          : [],
    })),
    closure_periods: draft.closurePeriods.map((closure) => ({
      starts_on: closure.startsOn,
      ends_on: closure.endsOn,
      reason: closure.reason.trim() || null,
    })),
  };
}
